import { useCallback, useEffect, useState } from 'react';
import { fetchInsolationHourlyByDay } from '../utils/solarInsolationHourly';

const REFRESH_MS = 30 * 60 * 1000;

export function useSolarInsolationHourly(latitude, longitude) {
  const [todayHourlyInsolation, setTodayHourlyInsolation] = useState(null);
  const [tomorrowHourlyInsolation, setTomorrowHourlyInsolation] = useState(null);
  const [todayPct, setTodayPct] = useState(null);
  const [tomorrowPct, setTomorrowPct] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyForecast = useCallback(next => {
    setTodayHourlyInsolation(next.today);
    setTomorrowHourlyInsolation(next.tomorrow);
    setTodayPct(next.todayPct ?? null);
    setTomorrowPct(next.tomorrowPct ?? null);
  }, []);

  const clearForecast = useCallback(() => {
    setTodayHourlyInsolation(null);
    setTomorrowHourlyInsolation(null);
    setTodayPct(null);
    setTomorrowPct(null);
    setLoading(false);
  }, []);

  const load = useCallback(async () => {
    const lat = Number(latitude);
    const lon = Number(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      clearForecast();
      return;
    }

    setLoading(true);
    const next = await fetchInsolationHourlyByDay(lat, lon);
    applyForecast(next);
    setLoading(false);
  }, [latitude, longitude, applyForecast, clearForecast]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const lat = Number(latitude);
      const lon = Number(longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        if (!cancelled) clearForecast();
        return;
      }
      if (!cancelled) setLoading(true);
      const next = await fetchInsolationHourlyByDay(lat, lon);
      if (!cancelled) {
        applyForecast(next);
        setLoading(false);
      }
    };

    run();
    const timerId = window.setInterval(run, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timerId);
    };
  }, [latitude, longitude, applyForecast, clearForecast]);

  return {
    todayHourlyInsolation,
    tomorrowHourlyInsolation,
    todayPct,
    tomorrowPct,
    /** @deprecated Use todayHourlyInsolation */
    hourlyInsolation: todayHourlyInsolation,
    loading,
    refresh: load,
  };
}
